<template>
  <div>
    <div class="bch-empty">
      <BcBubble v-if="gated || !rows.length"
                class="bch-sample"
                :parts="sampleParts" />
      <template v-if="gated">
        <p class="bch-line">{{ $t("broadcast.screens.home.empty_line") }}</p>
        <button type="button"
                class="btn btn-primary btn-lg w-100"
                disabled>{{ $t("broadcast.screens.home.create") }}</button>
        <button type="button"
                class="btn btn-outline-primary btn-lg w-100 mt-2"
                disabled>{{ $t("broadcast.screens.home.saved") }}</button>
        <p class="bch-line mt-3 mb-2">{{ $t("broadcast.screens.home.gate_line") }}</p>
        <button type="button"
                class="btn btn-primary btn-lg w-100"
                @click="$emit('connect')">{{ $t("broadcast.screens.home.connect") }}</button>
        <a v-if="guideUrl"
           class="btn btn-outline-primary btn-lg w-100 mt-2"
           :href="guideUrl"
           target="_blank"
           rel="noopener">{{ $t("broadcast.screens.home.guide") }}</a>
      </template>
      <template v-else>
        <p v-if="!rows.length"
           class="bch-line">{{ $t("broadcast.screens.home.empty_line") }}</p>
        <button type="button"
                class="btn btn-primary btn-lg w-100"
                @click="$emit('create')">{{ $t("broadcast.screens.home.create") }}</button>
        <button type="button"
                class="btn btn-outline-primary btn-lg w-100 mt-2"
                @click="$emit('saved')">{{ $t("broadcast.screens.home.saved") }}</button>
        <a v-if="guideUrl"
           class="btn btn-outline-primary btn-lg w-100 mt-2"
           :href="guideUrl"
           target="_blank"
           rel="noopener">{{ $t("broadcast.screens.home.guide") }}</a>
        <button type="button"
                class="btn btn-outline-primary btn-lg w-100 mt-2"
                @click="$emit('customers')">{{ $t("broadcast.screens.home.customers") }}</button>
      </template>
    </div>
    <div v-if="!gated && rows.length"
         class="bch-cards">
      <button v-for="row in rows"
              :key="row.id"
              type="button"
              class="bch-card"
              @click="$emit('open', row.id)">
        <span class="bch-top">
          <span class="bch-title">{{ titleOf(row) }}</span>
          <BcStatusPill :status="row.status"
                        :paused-reason="row.pausedReason" />
        </span>
        <span class="bch-sub">{{ whenOf(row.createdAt) }} · {{ $t("broadcast.screens.home.from_pages", { n: num(row.pages) }) }}</span>
        <span class="bch-nums">
          <span>{{ $t("broadcast.screens.home.delivered", { n: num(row.delivered != null ? row.delivered : 0) }) }}</span>
          <span>{{ $t("broadcast.screens.home.read", { n: num(row.read) }) }}</span>
          <span>{{ $t("broadcast.screens.home.failed", { n: num(row.failed) }) }}</span>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import BcBubble from "@/components/derived/broadcast/BcBubble";
import BcStatusPill from "@/components/derived/broadcast/BcStatusPill";
import { PREFILL, NAME_CHIP, SAMPLE, splitMarks } from "@/components/derived/broadcast/copy";

// The บรอดแคสต์ home. gated = no connected Page yet (the sample, two disabled buttons, connect).
// Otherwise: sample, the buttons, and the history cards (`rows` = GET /api/broadcasts).
// Emits create, saved, customers, connect, open(id).
export default {
  name: "BcHome",
  components: { BcBubble, BcStatusPill },
  props: {
    gated: { type: Boolean, default: false },
    rows: { type: Array, default: () => [] },
  },
  computed: {
    sampleParts() {
      // the ทุกคน prefill line with the sample name
      return splitMarks(PREFILL.all, [{ token: NAME_CHIP, show: SAMPLE.name, kind: "name" }]);
    },
    // The guide has no address in this app yet; the button appears when one is configured
    guideUrl() {
      return process.env.VUE_APP_BROADCAST_GUIDE_URL || "";
    },
  },
  methods: {
    num(n) {
      return Number(n || 0).toLocaleString("en-US");
    },
    titleOf(row) {
      return this.$t(row.kind === "utility" ? "broadcast.screens.home.prefix_all" : "broadcast.screens.home.prefix_24") + (row.groupsTh || "");
    },
    whenOf(iso) {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => ("0" + n).slice(-2);
      return pad(d.getDate()) + "/" + pad(d.getMonth() + 1) + "/" + d.getFullYear() + " " + pad(d.getHours()) + ":" + pad(d.getMinutes());
    },
  },
};
</script>

<style lang="scss" scoped>
.bch-sample {
  max-width: 340px;
}

.bch-line {
  color: #333;
  font-size: 1.05rem;
  margin: 1rem 0;
}

.btn {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.bch-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.bch-card {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  width: 100%;
  min-height: 44px;
  padding: 1rem 1.1rem;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 10px;
  color: #333;
  font-family: inherit;
  font-size: 1rem;

  &:hover {
    border-color: $brand-blue-mid;
  }
}

.bch-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.bch-title {
  font-weight: 700;
  font-size: 1.1rem;
  color: #333;
}

.bch-sub {
  color: #666;
  font-size: 0.875rem;
}

.bch-nums {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1.25rem;
  color: #333;
  font-size: 0.9375rem;
  margin-top: 0.25rem;
}

body.--dark {
  .bch-line,
  .bch-title,
  .bch-nums {
    color: #e6e6e6;
  }

  .bch-sub {
    color: #c4c4c4;
  }

  .bch-card {
    background: #4b4b4b;
    border-color: #555;
  }

  .btn.btn-outline-primary {
    color: #8cc8f5;
    border-color: #8cc8f5;
  }
}
</style>

